import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Users, FileText, Flag, Mail, HelpCircle, Megaphone, ScrollText,
  ShieldAlert, ExternalLink, BarChart3, Plus, Trash2, Pencil, Loader2, Ban, CheckCircle2, Search, ArrowUpDown, Paperclip,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { errMessage } from "@/lib/api";
import { TicketSheet } from "@/components/admin/TicketSheet";
import { TicketStatusBadge } from "@/components/admin/TicketStatusBadge";
import { AnalyticsTab } from "@/components/admin/AnalyticsTab";
import { TestFlagControl } from "@/components/admin/TestFlagControl";

const LOG_FILTERS = {
  all: null,
  logins: ["login_success"],
  registrations: ["user_registered"],
  projects: ["project_created", "project_published", "project_unpublished", "project_archived", "project_deleted"],
  admin: ["suspend_user", "activate_user", "publish_project", "unpublish_project"],
};

function fmtDateTime(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return null; }
}

function fmtDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return null; }
}

function Stat({ icon: Icon, label, value }) {
  return (
    <Card className="border-slate-200 shadow-sm">
      <CardContent className="flex items-center gap-3 p-4 sm:gap-4 sm:p-5">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-slate-100 sm:h-11 sm:w-11"><Icon className="h-5 w-5 text-[#0A2540]" /></div>
        <div className="min-w-0"><p className="text-xl font-bold text-slate-900 sm:text-2xl">{value}</p><p className="text-xs text-slate-500">{label}</p></div>
      </CardContent>
    </Card>
  );
}

const EVENT_LABELS = {
  user_registered: "User registered",
  login_success: "Successful login",
  project_created: "Project created",
  project_published: "Project published",
  project_unpublished: "Project unpublished",
  project_archived: "Project archived",
  project_deleted: "Project deleted",
  suspend_user: "User suspended (admin)",
  activate_user: "User activated (admin)",
  publish_project: "Project published (admin)",
  unpublish_project: "Project unpublished (admin)",
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [reports, setReports] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [ticketFilter, setTicketFilter] = useState("all");
  const [openTicket, setOpenTicket] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [logs, setLogs] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [userSort, setUserSort] = useState({ key: "created_at", dir: "desc" });
  const [logFilter, setLogFilter] = useState("all");
  const toggleSort = (key) => setUserSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" }));

  const [faqDialog, setFaqDialog] = useState(null); // {id?, question, answer, order}
  const [annDialog, setAnnDialog] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [s, u, p, r, c, f, a, l] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/users"), api.get("/admin/projects"),
        api.get("/admin/reports"), api.get("/admin/contacts"), api.get("/faqs"),
        api.get("/admin/announcements"), api.get("/admin/errors"),
      ]);
      setStats(s.data); setUsers(u.data); setProjects(p.data); setReports(r.data);
      setContacts(c.data); setFaqs(f.data); setAnnouncements(a.data); setLogs(l.data);
    } catch (e) { toast.error(errMessage(e)); }
  }, []);

  useEffect(() => { document.title = "Admin — CiteQR"; loadAll(); }, [loadAll]);

  const shownTickets = ticketFilter === "all" ? contacts : contacts.filter((c) => c.status === ticketFilter);
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); loadAll(); } catch (e) { toast.error(errMessage(e)); } };

  const displayedUsers = (() => {
    const q = userSearch.trim().toLowerCase();
    const arr = users.filter((u) => !q || (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q));
    const { key, dir } = userSort;
    const val = (u) => (key === "project_count" ? (u.project_count || 0) : (u[key] ? new Date(u[key]).getTime() : 0));
    return [...arr].sort((a, b) => (val(a) - val(b)) * (dir === "asc" ? 1 : -1));
  })();
  const SortTH = ({ label, k, className = "" }) => (
    <TableHead className={className}>
      <button type="button" onClick={() => toggleSort(k)} className="inline-flex items-center gap-1 hover:text-[#0D9488]" data-testid={`sort-${k}`}>
        {label}<ArrowUpDown className={`h-3.5 w-3.5 ${userSort.key === k ? "text-[#0D9488]" : "text-slate-300"}`} />
      </button>
    </TableHead>
  );

  const saveFaq = async () => {
    const body = { question: faqDialog.question, answer: faqDialog.answer, order: Number(faqDialog.order) || 0 };
    if (faqDialog.id) await api.put(`/admin/faqs/${faqDialog.id}`, body);
    else await api.post("/admin/faqs", body);
    setFaqDialog(null); toast.success("FAQ saved."); loadAll();
  };
  const saveAnn = async () => {
    const body = { title: annDialog.title, body: annDialog.body, active: annDialog.active };
    if (annDialog.id) await api.put(`/admin/announcements/${annDialog.id}`, body);
    else await api.post("/admin/announcements", body);
    setAnnDialog(null); toast.success("Announcement saved."); loadAll();
  };

  if (!stats) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0D9488]" /></div>;

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 fade-in">
      <div className="flex items-center gap-2">
        <ShieldAlert className="h-6 w-6 shrink-0 text-[#0A2540]" />
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Administrator dashboard</h1>
      </div>
      <p className="mt-2 text-base text-slate-600">Platform overview, moderation, and content management.</p>

      <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4">
        <Stat icon={Users} label="Users" value={stats.users} />
        <Stat icon={FileText} label="Projects" value={stats.projects} />
        <Stat icon={CheckCircle2} label="Published pages" value={stats.published_pages} />
        <Stat icon={Flag} label="Reported pages" value={stats.reported_pages} />
        <Stat icon={Ban} label="Suspended users" value={stats.suspended_users} />
        <Stat icon={Flag} label="Open reports (total)" value={stats.open_reports} />
        <Stat icon={Mail} label="Open tickets" value={stats.new_contact_messages} />
        <Stat icon={Mail} label="Total messages" value={stats.contact_messages} />
      </div>

      <Tabs defaultValue="users" className="mt-10">
        <TabsList className="flex h-auto w-full justify-start gap-1 overflow-x-auto">
          <TabsTrigger value="users" className="shrink-0" data-testid="tab-users"><Users className="mr-1.5 h-4 w-4" />Users</TabsTrigger>
          <TabsTrigger value="pages" className="shrink-0" data-testid="tab-pages"><FileText className="mr-1.5 h-4 w-4" />Pages</TabsTrigger>
          <TabsTrigger value="reports" className="shrink-0" data-testid="tab-reports"><Flag className="mr-1.5 h-4 w-4" />Reports</TabsTrigger>
          <TabsTrigger value="contacts" className="shrink-0" data-testid="tab-contacts"><Mail className="mr-1.5 h-4 w-4" />Contacts</TabsTrigger>
          <TabsTrigger value="faqs" className="shrink-0" data-testid="tab-faqs"><HelpCircle className="mr-1.5 h-4 w-4" />FAQs</TabsTrigger>
          <TabsTrigger value="announcements" className="shrink-0" data-testid="tab-announcements"><Megaphone className="mr-1.5 h-4 w-4" />Announcements</TabsTrigger>
          <TabsTrigger value="logs" className="shrink-0" data-testid="tab-logs"><ScrollText className="mr-1.5 h-4 w-4" />Logs</TabsTrigger>
          <TabsTrigger value="analytics" className="shrink-0" data-testid="tab-analytics"><BarChart3 className="mr-1.5 h-4 w-4" />Analytics</TabsTrigger>
        </TabsList>

        {/* Users */}
        <TabsContent value="users" className="mt-6">
          <div className="mb-4 flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs text-slate-600 sm:px-4 sm:py-3 sm:text-sm" data-testid="isolation-note">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-[#0D9488]" />
            <span><strong className="text-slate-800">Account-isolated data:</strong> Each account can view and manage only its own projects and reference pages. Project counts reflect content owned by that account.</span>
          </div>
          <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative w-full sm:max-w-sm">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input value={userSearch} onChange={(e) => setUserSearch(e.target.value)} placeholder="Search by name or email…" className="w-full pl-9" data-testid="user-search" />
            </div>
            <span className="shrink-0 whitespace-nowrap text-sm text-slate-500" data-testid="user-count">{displayedUsers.length} of {users.length} users</span>
          </div>

          {/* Mobile: stacked card per user */}
          <div className="space-y-3 md:hidden" data-testid="user-cards-mobile">
            {displayedUsers.map((u) => (
              <Card key={u.id} className="border-slate-200 shadow-sm" data-testid={`admin-user-card-${u.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-slate-900" data-testid={`user-name-m-${u.id}`}>{u.name}</p>
                      <p className="mt-0.5 break-all text-xs text-slate-500" data-testid={`user-email-m-${u.id}`}>{u.email}</p>
                    </div>
                    <Badge variant={u.status === "active" ? "secondary" : "destructive"} className="shrink-0" data-testid={`user-status-m-${u.id}`}>{u.status}</Badge>
                  </div>
                  <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2.5 text-xs">
                    <div>
                      <dt className="text-slate-400">Signup method</dt>
                      <dd className="mt-1" data-testid={`user-signup-m-${u.id}`}>
                        {u.auth_provider === "google"
                          ? <Badge variant="outline" className="border-blue-300 bg-blue-50 text-blue-700">Google</Badge>
                          : <Badge variant="outline" className="border-slate-300 bg-slate-50 text-slate-700">Email</Badge>}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-slate-400">Role</dt>
                      <dd className="mt-1"><Badge variant={u.role === "admin" ? "default" : "secondary"} className={u.role === "admin" ? "bg-[#0A2540]" : ""}>{u.role}</Badge></dd>
                    </div>
                    <div>
                      <dt className="text-slate-400">Projects</dt>
                      <dd className="mt-1 font-medium text-slate-700" data-testid={`user-projects-m-${u.id}`}>{u.project_count}</dd>
                    </div>
                    <div>
                      <dt className="text-slate-400">Created</dt>
                      <dd className="mt-1 text-slate-600" data-testid={`user-created-m-${u.id}`}>{fmtDateTime(u.created_at) || "—"}</dd>
                    </div>
                    <div className="col-span-2">
                      <dt className="text-slate-400">Analytics</dt>
                      <dd className="mt-1"><TestFlagControl user={u} onChange={loadAll} /></dd>
                    </div>
                    <div className="col-span-2">
                      <dt className="text-slate-400">Last login</dt>
                      <dd className="mt-1 text-slate-600" data-testid={`user-lastlogin-m-${u.id}`}>{fmtDateTime(u.last_login) || "Not tracked"}</dd>
                    </div>
                  </dl>
                  {u.role !== "admin" && (
                    <div className="mt-3">
                      {u.status === "active" ? (
                        <Button size="sm" variant="outline" className="w-full text-red-600" onClick={() => act(() => api.post(`/admin/users/${u.id}/suspend`), "User suspended")} data-testid={`suspend-m-${u.id}`}><Ban className="mr-1 h-3.5 w-3.5" />Suspend</Button>
                      ) : (
                        <Button size="sm" variant="outline" className="w-full" onClick={() => act(() => api.post(`/admin/users/${u.id}/activate`), "User activated")} data-testid={`activate-m-${u.id}`}>Activate</Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Desktop/tablet: full sortable table */}
          <Card className="hidden border-slate-200 shadow-sm md:block"><CardContent className="overflow-x-auto p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Signup method</TableHead>
                <TableHead>Role</TableHead>
                <SortTH label="Created" k="created_at" />
                <SortTH label="Projects" k="project_count" />
                <TableHead>Status</TableHead>
                <SortTH label="Last login" k="last_login" />
                <TableHead>Analytics</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {displayedUsers.map((u) => (
                  <TableRow key={u.id} data-testid={`admin-user-${u.id}`}>
                    <TableCell className="max-w-[160px] break-words font-medium" data-testid={`user-name-${u.id}`}>{u.name}</TableCell>
                    <TableCell className="max-w-[210px] break-all text-slate-500" data-testid={`user-email-${u.id}`}>{u.email}</TableCell>
                    <TableCell data-testid={`user-signup-${u.id}`}>
                      {u.auth_provider === "google" ? (
                        <Badge variant="outline" className="border-blue-300 bg-blue-50 text-blue-700">Google</Badge>
                      ) : (
                        <Badge variant="outline" className="border-slate-300 bg-slate-50 text-slate-700">Email</Badge>
                      )}
                    </TableCell>
                    <TableCell><Badge variant={u.role === "admin" ? "default" : "secondary"} className={u.role === "admin" ? "bg-[#0A2540]" : ""}>{u.role}</Badge></TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-slate-600" data-testid={`user-created-${u.id}`}>{fmtDate(u.created_at) || <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell data-testid={`user-projects-${u.id}`}>{u.project_count}</TableCell>
                    <TableCell><Badge variant={u.status === "active" ? "secondary" : "destructive"}>{u.status}</Badge></TableCell>
                    <TableCell className="whitespace-nowrap text-sm" data-testid={`user-lastlogin-${u.id}`}>
                      {fmtDateTime(u.last_login) || <span className="text-slate-400">Not tracked</span>}
                    </TableCell>
                    <TableCell><TestFlagControl user={u} onChange={loadAll} /></TableCell>
                    <TableCell className="text-right">
                      {u.role !== "admin" && (u.status === "active" ? (
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => act(() => api.post(`/admin/users/${u.id}/suspend`), "User suspended")} data-testid={`suspend-${u.id}`}><Ban className="mr-1 h-3.5 w-3.5" />Suspend</Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => act(() => api.post(`/admin/users/${u.id}/activate`), "User activated")} data-testid={`activate-${u.id}`}>Activate</Button>
                      ))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="analytics" className="mt-6"><AnalyticsTab /></TabsContent>

        {/* Pages */}
        <TabsContent value="pages" className="mt-6">
          <Card className="border-slate-200 shadow-sm"><CardContent className="overflow-x-auto p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Status</TableHead><TableHead>Refs</TableHead><TableHead>Reports</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
              <TableBody>
                {projects.map((p) => (
                  <TableRow key={p.id} className={p.reported ? "bg-amber-50" : ""} data-testid={`admin-page-${p.id}`}>
                    <TableCell className="max-w-[220px] truncate font-medium">{p.title}</TableCell>
                    <TableCell><Badge variant={p.status === "published" ? "default" : "secondary"} className={p.status === "published" ? "bg-[#0D9488]" : ""}>{p.status}</Badge></TableCell>
                    <TableCell>{p.reference_count}</TableCell>
                    <TableCell>{p.report_count > 0 ? <Badge variant="destructive">{p.report_count}</Badge> : "—"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button asChild size="sm" variant="ghost"><a href={`/r/${p.public_id}`} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-4 w-4" /></a></Button>
                        {p.status === "published" && <Button size="sm" variant="outline" className="text-red-600" onClick={() => act(() => api.post(`/admin/projects/${p.id}/unpublish`), "Page unpublished")} data-testid={`unpublish-${p.id}`}>Unpublish</Button>}
                        {p.status === "unpublished" && <Button size="sm" variant="outline" onClick={() => act(() => api.post(`/admin/projects/${p.id}/publish`), "Page published")} data-testid={`publish-${p.id}`}>Publish</Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        {/* Reports */}
        <TabsContent value="reports" className="mt-6">
          <Card className="border-slate-200 shadow-sm"><CardContent className="overflow-x-auto p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Reason</TableHead><TableHead>Page</TableHead><TableHead>Reporter</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
              <TableBody>
                {reports.length === 0 ? <TableRow><TableCell colSpan={5} className="py-8 text-center text-slate-500">No reports.</TableCell></TableRow> :
                  reports.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="max-w-[280px] truncate">{r.reason}</TableCell>
                      <TableCell><a href={`/r/${r.public_id}`} target="_blank" rel="noopener noreferrer" className="text-[#0D9488] underline">{r.public_id}</a></TableCell>
                      <TableCell className="text-slate-500">{r.reporter_email || "—"}</TableCell>
                      <TableCell><Badge variant={r.status === "open" ? "destructive" : "secondary"}>{r.status}</Badge></TableCell>
                      <TableCell className="text-right">{r.status === "open" && <Button size="sm" variant="outline" onClick={() => act(() => api.post(`/admin/reports/${r.id}/resolve`), "Report resolved")}>Resolve</Button>}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        {/* Contacts */}
        <TabsContent value="contacts" className="mt-6">
          <div className="mb-4 flex flex-wrap gap-2" data-testid="ticket-filters">
            {["all", "open", "pending", "resolved"].map((f) => (
              <Button key={f} size="sm" variant={ticketFilter === f ? "default" : "outline"} onClick={() => setTicketFilter(f)}
                className={ticketFilter === f ? "bg-[#0A2540] capitalize" : "capitalize"} data-testid={`ticket-filter-${f}`}>
                {f} <span className="ml-1.5 text-xs opacity-70">{f === "all" ? contacts.length : contacts.filter((c) => c.status === f).length}</span>
              </Button>
            ))}
          </div>
          <Card className="border-slate-200 shadow-sm"><CardContent className="overflow-x-auto p-0">
            <Table>
              <TableHeader><TableRow><TableHead>From</TableHead><TableHead>Category</TableHead><TableHead>Subject</TableHead><TableHead>Thread</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
              <TableBody>
                {shownTickets.length === 0 ? <TableRow><TableCell colSpan={6} className="py-8 text-center text-slate-500">No tickets.</TableCell></TableRow> :
                  shownTickets.map((c) => (
                    <TableRow key={c.id} data-testid={`ticket-row-${c.id}`}>
                      <TableCell><div className="font-medium">{c.name}</div><div className="text-xs text-slate-500">{c.email}</div></TableCell>
                      <TableCell data-testid={`contact-category-${c.id}`}>
                        {c.category
                          ? <Badge variant="outline" className="border-[#0D9488]/40 bg-[#0D9488]/10 text-[#0D9488]">{c.category}</Badge>
                          : <span className="text-xs text-slate-400">Contact form</span>}
                      </TableCell>
                      <TableCell className="max-w-[260px]"><div className="truncate font-medium">{c.subject}</div><div className="font-mono text-xs text-slate-400">{c.ticket_ref}</div></TableCell>
                      <TableCell className="text-xs text-slate-600">
                        {c.message_count} message{c.message_count === 1 ? "" : "s"}
                        {c.attachment_count > 0 && <span className="ml-2 inline-flex items-center gap-0.5 text-[#0D9488]"><Paperclip className="h-3 w-3" />{c.attachment_count}</span>}
                        {c.last_direction === "customer" && c.message_count > 1 && <div className="text-[#0D9488]">Customer replied</div>}
                      </TableCell>
                      <TableCell><TicketStatusBadge status={c.status} testId={`ticket-status-${c.id}`} /></TableCell>
                      <TableCell className="text-right"><Button size="sm" variant="outline" onClick={() => setOpenTicket(c.id)} data-testid={`ticket-open-${c.id}`}>View</Button></TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </CardContent></Card>
          <TicketSheet ticketId={openTicket} onClose={() => setOpenTicket(null)} onUpdated={loadAll} />
        </TabsContent>

        {/* FAQs */}
        <TabsContent value="faqs" className="mt-6">
          <div className="mb-4 flex justify-end">
            <Button onClick={() => setFaqDialog({ question: "", answer: "", order: faqs.length })} className="bg-[#0D9488] text-white hover:bg-[#0F766E]" data-testid="add-faq-btn"><Plus className="mr-2 h-4 w-4" />Add FAQ</Button>
          </div>
          <div className="space-y-3" data-testid="faq-list">
            {faqs.length === 0 ? <p className="text-sm text-slate-500">No FAQs.</p> :
              faqs.map((f) => (
              <Card key={f.id} className="border-slate-200 shadow-sm"><CardContent className="flex items-start justify-between gap-4 p-5">
                <div><p className="font-medium text-slate-900">{f.question}</p><p className="mt-1 text-sm text-slate-600">{f.answer}</p></div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" onClick={() => setFaqDialog(f)} aria-label="Edit FAQ" data-testid={`edit-faq-${f.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => act(() => api.delete(`/admin/faqs/${f.id}`), "FAQ deleted")} aria-label="Delete FAQ" data-testid={`delete-faq-${f.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              </CardContent></Card>
            ))}
          </div>
        </TabsContent>

        {/* Announcements */}
        <TabsContent value="announcements" className="mt-6">
          <div className="mb-4 flex justify-end">
            <Button onClick={() => setAnnDialog({ title: "", body: "", active: true })} className="bg-[#0D9488] text-white hover:bg-[#0F766E]" data-testid="add-announcement-btn"><Plus className="mr-2 h-4 w-4" />Add announcement</Button>
          </div>
          <div className="space-y-3">
            {announcements.length === 0 ? <p className="text-sm text-slate-500">No announcements.</p> :
              announcements.map((a) => (
                <Card key={a.id} className="border-slate-200 shadow-sm"><CardContent className="flex items-start justify-between gap-4 p-5">
                  <div><div className="flex items-center gap-2"><p className="font-medium text-slate-900">{a.title}</p>{a.active ? <Badge className="bg-[#0D9488]">active</Badge> : <Badge variant="secondary">hidden</Badge>}</div><p className="mt-1 text-sm text-slate-600">{a.body}</p></div>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={() => setAnnDialog(a)} aria-label="Edit announcement" data-testid={`edit-ann-${a.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => act(() => api.delete(`/admin/announcements/${a.id}`), "Deleted")} aria-label="Delete announcement" data-testid={`delete-ann-${a.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                </CardContent></Card>
              ))}
          </div>
        </TabsContent>

        {/* Logs */}
        <TabsContent value="logs" className="mt-6">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-slate-500" data-testid="logs-note">Meaningful platform events (registrations, logins, project lifecycle). No passwords, tokens, or reference contents are recorded.</p>
            <Select value={logFilter} onValueChange={setLogFilter}>
              <SelectTrigger className="w-56" data-testid="log-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="log-filter-all">All events</SelectItem>
                <SelectItem value="logins" data-testid="log-filter-logins">Logins</SelectItem>
                <SelectItem value="registrations" data-testid="log-filter-registrations">Registrations</SelectItem>
                <SelectItem value="projects" data-testid="log-filter-projects">Project events</SelectItem>
                <SelectItem value="admin" data-testid="log-filter-admin">Admin actions</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Card className="border-slate-200 shadow-sm"><CardContent className="overflow-x-auto p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Event</TableHead><TableHead>Account ID</TableHead><TableHead>Project ID</TableHead><TableHead>When</TableHead></TableRow></TableHeader>
              <TableBody>
                {(() => {
                  const allow = LOG_FILTERS[logFilter];
                  const rows = logs.filter((l) => !allow || allow.includes(l.action));
                  return rows.length === 0 ? <TableRow><TableCell colSpan={4} className="py-8 text-center text-slate-500">No events for this filter.</TableCell></TableRow> :
                    rows.map((l) => (
                      <TableRow key={l.id} data-testid={`log-${l.id}`}>
                        <TableCell><Badge variant="secondary" className="font-normal">{EVENT_LABELS[l.action] || l.action}</Badge></TableCell>
                        <TableCell className="font-mono text-xs text-slate-500">{l.user_id || l.admin || "—"}</TableCell>
                        <TableCell className="font-mono text-xs text-slate-500">{l.project_id || l.target || "—"}</TableCell>
                        <TableCell className="whitespace-nowrap text-slate-500">{l.at ? new Date(l.at).toLocaleString() : ""}</TableCell>
                      </TableRow>
                    ));
                })()}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      {/* FAQ dialog */}
      <Dialog open={!!faqDialog} onOpenChange={(o) => !o && setFaqDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{faqDialog?.id ? "Edit FAQ" : "Add FAQ"}</DialogTitle></DialogHeader>
          {faqDialog && (
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Question</Label><Input value={faqDialog.question} onChange={(e) => setFaqDialog({ ...faqDialog, question: e.target.value })} data-testid="faq-question-input" /></div>
              <div className="space-y-1.5"><Label>Answer</Label><Textarea rows={4} value={faqDialog.answer} onChange={(e) => setFaqDialog({ ...faqDialog, answer: e.target.value })} data-testid="faq-answer-input" /></div>
              <div className="space-y-1.5"><Label>Order</Label><Input type="number" value={faqDialog.order} onChange={(e) => setFaqDialog({ ...faqDialog, order: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter><Button onClick={saveFaq} className="bg-[#0A2540] text-white hover:bg-[#0c2e4e]" data-testid="faq-save-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Announcement dialog */}
      <Dialog open={!!annDialog} onOpenChange={(o) => !o && setAnnDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{annDialog?.id ? "Edit announcement" : "Add announcement"}</DialogTitle></DialogHeader>
          {annDialog && (
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Title</Label><Input value={annDialog.title} onChange={(e) => setAnnDialog({ ...annDialog, title: e.target.value })} data-testid="ann-title-input" /></div>
              <div className="space-y-1.5"><Label>Body</Label><Textarea rows={3} value={annDialog.body} onChange={(e) => setAnnDialog({ ...annDialog, body: e.target.value })} data-testid="ann-body-input" /></div>
              <div className="flex items-center gap-2"><Switch checked={annDialog.active} onCheckedChange={(v) => setAnnDialog({ ...annDialog, active: v })} id="ann-active" /><Label htmlFor="ann-active">Active (show on homepage)</Label></div>
            </div>
          )}
          <DialogFooter><Button onClick={saveAnn} className="bg-[#0A2540] text-white hover:bg-[#0c2e4e]" data-testid="ann-save-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
